/**
 * Generateur — Utiliser la somme des angles d'un triangle pour calculer des angles,
 *              effectuer des constructions et resoudre des problemes
 * Attendu ID : 669 | Niveau : 6eme | Domaine : Espace et geometrie
 *
 * Template unique : calcul_angles_triangle
 * Formats : qcm_unique, texte, tableau_vf, texte_trou_drag, texte_trou_saisie
 *
 * Sous-variantes QCM : quelconque, isocele_sommet, isocele_base, rectangle, nature
 * Sous-variantes saisie : quelconque, isocele, rectangle
 * Sous-variantes VF : triangle quelconque avec proprietes
 * Sous-variantes drag : 3 modeles (quelconque, isocele, rectangle)
 * Sous-variantes saisie trous : 4 modeles
 *
 * Variantes estimees : 500+ (objectif : 20/20 uniques sur 20 iterations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 669;
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');
const pickLetters = (n) => pickLettresSains(LETTRES, n);

/** Encode une chaîne SVG en data URL (utf8 + encodeURIComponent). */
const svgToDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

const L_CALC = 'Calculer des angles dans un triangle en utilisant la somme des angles';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_CALC,
});

// ─── Pools de valeurs ────────────────────────────────────────

const genQuelconque = () => {
    for (let i = 0; i < 50; i++) {
        const a = randInt(15, 80);
        const b = randInt(15, 80);
        const c = 180 - a - b;
        if (c >= 15) return { a, b, c };
    }
    return { a: 50, b: 60, c: 70 };
};

// s even in [10,160], exclude 60 half the time for variety
const ANGLES_ISOCELE_SOMMET = [];
for (let s = 10; s <= 160; s += 2) ANGLES_ISOCELE_SOMMET.push(s);

const ANGLES_ISOCELE_BASE = [];
for (let b = 10; b <= 85; b += 5) ANGLES_ISOCELE_BASE.push(b);

const ANGLES_RECTANGLE = [];
for (let a = 5; a <= 85; a += 5) ANGLES_RECTANGLE.push(a);

// ─── Natures de triangle ────────────────────────────────────

const natureTriangle = (a, b, c) => {
    const sorted = [a, b, c].sort((x, y) => x - y);
    if (a === 60 && b === 60 && c === 60) return 'equilateral';
    if (sorted[2] === 90 && sorted[0] === sorted[1]) return 'rectangle isocele';
    if (sorted[2] === 90) return 'rectangle';
    if (a === b || b === c || a === c) return 'isocele';
    return 'quelconque';
};

const natureLabel = (n) => {
    const map = {
        'quelconque': 'quelconque',
        'isocele': 'isocèle',
        'equilateral': 'équilatéral',
        'rectangle': 'rectangle',
        'rectangle isocele': 'rectangle isocèle',
    };
    return map[n] || n;
};

// ─── SVG Helper ──────────────────────────────────────────────

/**
 * Draws a triangle SVG with labeled vertices, optional angle values,
 * and optional tick marks for isoceles sides.
 *
 * @param {string[]} names - 3 vertex names [A, B, C]
 * @param {number[]} angles - 3 angle values [angleA, angleB, angleC]
 * @param {boolean[]} showAngles - which angles to show as values (true) or "?" (false)
 * @param {object} options - { isocele: null | vertexIndex }
 */
const svgTriangle = (names, angles, showAngles, options = {}) => {
    const W = 300, H = 200;
    const cx = 150, cy = 110;
    const toRad = (d) => d * Math.PI / 180;

    // Place vertices: bottom-left, bottom-right, top
    // Use angle C at top, A at bottom-left, B at bottom-right
    const baseLen = 120;
    const heightVal = 80 + randInt(0, 20);

    const Ax = cx - baseLen / 2;
    const Ay = cy + 40;
    const Bx = cx + baseLen / 2;
    const By = cy + 40;
    // Shift top vertex horizontally based on angles for more realistic look
    const topShift = (angles[0] - angles[1]) * 0.4;
    const Cx = cx + topShift;
    const Cy = cy + 40 - heightVal;

    const pts = [
        { x: Ax, y: Ay },
        { x: Bx, y: By },
        { x: Cx, y: Cy },
    ];

    // Label offsets
    const labelOffsets = [
        { dx: -14, dy: 16 },
        { dx: 6, dy: 16 },
        { dx: -4, dy: -10 },
    ];

    let svg = `<svg viewBox='0 0 ${W} ${H}' xmlns='http://www.w3.org/2000/svg'>`;

    // Triangle edges
    for (let i = 0; i < 3; i++) {
        const j = (i + 1) % 3;
        svg += `<line x1='${pts[i].x.toFixed(1)}' y1='${pts[i].y.toFixed(1)}' x2='${pts[j].x.toFixed(1)}' y2='${pts[j].y.toFixed(1)}' stroke='black' stroke-width='2'/>`;
    }

    // Angle arcs and labels
    for (let i = 0; i < 3; i++) {
        const prev = (i + 2) % 3;
        const next = (i + 1) % 3;
        const vx = pts[i].x, vy = pts[i].y;

        // Compute angle directions
        const a1 = Math.atan2(pts[prev].y - vy, pts[prev].x - vx);
        const a2 = Math.atan2(pts[next].y - vy, pts[next].x - vx);

        const arcR = 22;
        const startX = vx + arcR * Math.cos(a1);
        const startY = vy + arcR * Math.sin(a1);
        const endX = vx + arcR * Math.cos(a2);
        const endY = vy + arcR * Math.sin(a2);

        // Determine sweep direction (always draw the smaller arc)
        let diff = a2 - a1;
        if (diff > Math.PI) diff -= 2 * Math.PI;
        if (diff < -Math.PI) diff += 2 * Math.PI;
        const sweepFlag = diff > 0 ? 1 : 0;

        const color = showAngles[i] ? '#888' : '#E24B4A';
        svg += `<path d='M ${startX.toFixed(1)} ${startY.toFixed(1)} A ${arcR} ${arcR} 0 0 ${sweepFlag} ${endX.toFixed(1)} ${endY.toFixed(1)}' fill='none' stroke='${color}' stroke-width='1.2'/>`;

        // Angle text
        const midAngle = (a1 + a2) / 2;
        // Correct midAngle to bisect the interior angle
        const testX = vx + 10 * Math.cos(midAngle);
        const testY = vy + 10 * Math.sin(midAngle);
        // Check if midpoint is inside triangle roughly (toward centroid)
        const centroidX = (pts[0].x + pts[1].x + pts[2].x) / 3;
        const centroidY = (pts[0].y + pts[1].y + pts[2].y) / 3;
        const towardCentroid = Math.atan2(centroidY - vy, centroidX - vx);
        let finalMidAngle = midAngle;
        // If midAngle points away from centroid, flip it
        let angleDiffCheck = finalMidAngle - towardCentroid;
        if (angleDiffCheck > Math.PI) angleDiffCheck -= 2 * Math.PI;
        if (angleDiffCheck < -Math.PI) angleDiffCheck += 2 * Math.PI;
        if (Math.abs(angleDiffCheck) > Math.PI / 2) {
            finalMidAngle = midAngle + Math.PI;
        }

        const textR = arcR + 12;
        const tx = vx + textR * Math.cos(finalMidAngle);
        const ty = vy + textR * Math.sin(finalMidAngle);
        const angleText = showAngles[i] ? `${angles[i]}°` : '?';
        const textColor = showAngles[i] ? '#888' : '#E24B4A';
        svg += `<text x='${tx.toFixed(1)}' y='${ty.toFixed(1)}' font-size='11' fill='${textColor}' text-anchor='middle' dominant-baseline='central'>${angleText}</text>`;
    }

    // Vertex labels
    for (let i = 0; i < 3; i++) {
        svg += `<text x='${(pts[i].x + labelOffsets[i].dx).toFixed(1)}' y='${(pts[i].y + labelOffsets[i].dy).toFixed(1)}' font-size='14' font-weight='bold'>${names[i]}</text>`;
    }

    // Isocele tick marks
    if (options.isocele != null) {
        const vi = options.isocele; // vertex index of the apex
        const s1 = (vi + 1) % 3;
        const s2 = (vi + 2) % 3;
        // Draw tick marks on sides vi-s1 and vi-s2
        const drawTick = (p1, p2) => {
            const mx = (p1.x + p2.x) / 2;
            const my = (p1.y + p2.y) / 2;
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            const nx = -dy / len * 6;
            const ny = dx / len * 6;
            return `<line x1='${(mx - nx).toFixed(1)}' y1='${(my - ny).toFixed(1)}' x2='${(mx + nx).toFixed(1)}' y2='${(my + ny).toFixed(1)}' stroke='#1D9E75' stroke-width='2'/>`;
        };
        svg += drawTick(pts[vi], pts[s1]);
        svg += drawTick(pts[vi], pts[s2]);
    }

    // Right angle mark
    if (options.rightAngleAt != null) {
        const ri = options.rightAngleAt;
        const prev = (ri + 2) % 3;
        const next = (ri + 1) % 3;
        const vx = pts[ri].x, vy = pts[ri].y;
        const d1x = pts[prev].x - vx, d1y = pts[prev].y - vy;
        const d2x = pts[next].x - vx, d2y = pts[next].y - vy;
        const len1 = Math.sqrt(d1x * d1x + d1y * d1y);
        const len2 = Math.sqrt(d2x * d2x + d2y * d2y);
        const sz = 12;
        const u1x = d1x / len1 * sz, u1y = d1y / len1 * sz;
        const u2x = d2x / len2 * sz, u2y = d2y / len2 * sz;
        svg += `<polyline points='${(vx + u1x).toFixed(1)},${(vy + u1y).toFixed(1)} ${(vx + u1x + u2x).toFixed(1)},${(vy + u1y + u2y).toFixed(1)} ${(vx + u2x).toFixed(1)},${(vy + u2y).toFixed(1)}' fill='none' stroke='black' stroke-width='1.2'/>`;
    }

    svg += `</svg>`;
    return svg;
};

// ─── Helper: attach SVG as element ──────────────────────────

const withSvg = (payload, svg) => {
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'image', config: { imageData: svgToDataUrl(svg) } }],
    };
    return payload;
};

// ─── Helper: unique QCM choices ─────────────────────────────

const buildUniqueChoices = (correct, pieges, count = 4) => {
    const seen = new Set();
    seen.add(String(correct));
    const choices = [String(correct)];
    for (const p of pieges) {
        const s = String(p);
        const numVal = parseFloat(s);
        if (seen.has(s)) continue;
        if (!isNaN(numVal) && (numVal <= 0 || numVal > 180)) continue;
        seen.add(s);
        choices.push(s);
        if (choices.length >= count) break;
    }
    // If not enough, add fallbacks
    const fallbacks = [15, 25, 35, 55, 65, 75, 85, 95, 105, 115, 125, 135, 145, 155];
    for (const f of shuffle(fallbacks)) {
        if (choices.length >= count) break;
        const s = `${f}`;
        if (!seen.has(s)) {
            seen.add(s);
            choices.push(s);
        }
    }
    return choices;
};

// ─── QCM UNIQUE (5 sub-variants) ────────────────────────────

const genQcmQuelconque = () => {
    const [X, V, Y] = pickLetters(3);
    const { a, b, c } = genQuelconque();
    const texte = `<p>Dans le triangle ${X}${V}${Y}, l'angle en ${X} mesure ${a}° et l'angle en ${V} mesure ${b}°. Quelle est la mesure de l'angle en ${Y} ?</p>{{element:0}}`;
    const correct = `${c}`;
    const pieges = [`${a + b}`, `${360 - a - b}`, `${180 - a}`, `${180 - b}`];
    const choices = buildUniqueChoices(correct, pieges);
    const svg = svgTriangle([X, V, Y], [a, b, c], [true, true, false]);
    const payload = makeQcm(choices.map(v => `${v}°`), `${correct}°`, texte, opts());
    withSvg(payload, svg);
    return payload;
};

const genQcmIsoceleSommet = () => {
    const [X, V, Y] = pickLetters(3);
    const s = randomChoice(ANGLES_ISOCELE_SOMMET);
    const base = (180 - s) / 2;
    const texte = `<p>Le triangle ${X}${V}${Y} est isocèle en ${X}. L'angle en ${X} mesure ${s}°. Quelle est la mesure de l'angle en ${V} ?</p>{{element:0}}`;
    const correct = `${base}`;
    const pieges = [`${180 - s}`, `${s / 2}`, `${s}`, `${90 - s / 2}`];
    const choices = buildUniqueChoices(correct, pieges);
    const svg = svgTriangle([X, V, Y], [s, base, base], [true, false, false], { isocele: 0 });
    const payload = makeQcm(choices.map(v => `${v}°`), `${correct}°`, texte, opts());
    withSvg(payload, svg);
    return payload;
};

const genQcmIsoceleBase = () => {
    const [X, V, Y] = pickLetters(3);
    const b = randomChoice(ANGLES_ISOCELE_BASE);
    const sommet = 180 - 2 * b;
    const texte = `<p>Le triangle ${X}${V}${Y} est isocèle en ${X}. L'angle en ${V} mesure ${b}°. Quelle est la mesure de l'angle en ${X} ?</p>{{element:0}}`;
    const correct = `${sommet}`;
    const pieges = [`${180 - b}`, `${b}`, `${2 * b}`, `${90 - b}`];
    const choices = buildUniqueChoices(correct, pieges);
    const svg = svgTriangle([X, V, Y], [sommet, b, b], [false, true, false], { isocele: 0 });
    const payload = makeQcm(choices.map(v => `${v}°`), `${correct}°`, texte, opts());
    withSvg(payload, svg);
    return payload;
};

const genQcmRectangle = () => {
    const [X, V, Y] = pickLetters(3);
    const a = randomChoice(ANGLES_RECTANGLE);
    const other = 90 - a;
    const texte = `<p>Le triangle ${X}${V}${Y} est rectangle en ${V}. L'angle en ${X} mesure ${a}°. Quelle est la mesure de l'angle en ${Y} ?</p>{{element:0}}`;
    const correct = `${other}`;
    const pieges = [`${180 - a}`, `${a}`, `90`, `${180 - 2 * a}`];
    const choices = buildUniqueChoices(correct, pieges);
    const svg = svgTriangle([X, V, Y], [a, 90, other], [true, true, false], { rightAngleAt: 1 });
    const payload = makeQcm(choices.map(v => `${v}°`), `${correct}°`, texte, opts());
    withSvg(payload, svg);
    return payload;
};

const genQcmNature = () => {
    const [X, V, Y] = pickLetters(3);
    // Pick a random type of triangle
    const type = randomChoice(['quelconque', 'isocele', 'equilateral', 'rectangle', 'rectangle_isocele']);
    let a, b, c;
    if (type === 'quelconque') {
        ({ a, b, c } = genQuelconque());
        // Ensure it's truly quelconque (no equal angles, no 90)
        if (a === b || b === c || a === c || a === 90 || b === 90 || c === 90) {
            return genQcmNature(); // retry
        }
    } else if (type === 'isocele') {
        const s = randomChoice(ANGLES_ISOCELE_SOMMET.filter(v => v !== 60 && v !== 90));
        const base = (180 - s) / 2;
        // Randomly assign which vertex gets the apex angle
        const perm = shuffle([s, base, base]);
        [a, b, c] = perm;
    } else if (type === 'equilateral') {
        a = 60; b = 60; c = 60;
    } else if (type === 'rectangle') {
        const acute = randomChoice(ANGLES_RECTANGLE.filter(v => v !== 45));
        const other = 90 - acute;
        const perm = shuffle([90, acute, other]);
        [a, b, c] = perm;
    } else {
        // rectangle isocele
        a = 90; b = 45; c = 45;
        const perm = shuffle([a, b, c]);
        [a, b, c] = perm;
    }

    const nat = natureTriangle(a, b, c);
    const texte = `<p>Un triangle ${X}${V}${Y} a des angles mesurant ${a}°, ${b}° et ${c}°. Quelle est la nature de ce triangle ?</p>`;
    const correct = natureLabel(nat);
    const allNatures = ['quelconque', 'isocèle', 'équilatéral', 'rectangle', 'rectangle isocèle'];
    const wrongNatures = shuffle(allNatures.filter(n => n !== correct)).slice(0, 3);
    const choices = shuffle([correct, ...wrongNatures]);
    const payload = makeQcm(choices, correct, texte, opts());
    return payload;
};

const genQcm = () => {
    const variant = randomChoice(['quelconque', 'isocele_sommet', 'isocele_base', 'rectangle', 'nature']);
    switch (variant) {
        case 'quelconque': return genQcmQuelconque();
        case 'isocele_sommet': return genQcmIsoceleSommet();
        case 'isocele_base': return genQcmIsoceleBase();
        case 'rectangle': return genQcmRectangle();
        case 'nature': return genQcmNature();
    }
};

// ─── SAISIE (3 sub-variants) ────────────────────────────────

const genSaisieQuelconque = () => {
    const [X, V, Y] = pickLetters(3);
    const { a, b, c } = genQuelconque();
    const texte = `<p>Dans le triangle ${X}${V}${Y}, l'angle en ${X} mesure ${a}° et l'angle en ${V} mesure ${b}°. Quelle est la mesure, en degrés, de l'angle en ${Y} ?</p>{{element:0}}`;
    const svg = svgTriangle([X, V, Y], [a, b, c], [true, true, false]);
    // `suffixe` affiche l'unité À CÔTÉ du champ : l'élève voit qu'il n'a que le
    // nombre à taper. Sans lui, celui qui écrit « 40° » était refusé, le moteur
    // de correction ne retirant pas les unités.
    const payload = makeSaisie(String(c), texte, { ...opts(), strict: true, suffixe: '°' });
    withSvg(payload, svg);
    return payload;
};

const genSaisieIsocele = () => {
    const [X, V, Y] = pickLetters(3);
    const sub = randomChoice(['sommet', 'base']);
    if (sub === 'sommet') {
        const s = randomChoice(ANGLES_ISOCELE_SOMMET);
        const base = (180 - s) / 2;
        const texte = `<p>Le triangle ${X}${V}${Y} est isocèle en ${X}. L'angle en ${X} mesure ${s}°. Quelle est la mesure, en degrés, de l'angle en ${V} ?</p>{{element:0}}`;
        const svg = svgTriangle([X, V, Y], [s, base, base], [true, false, false], { isocele: 0 });
        const payload = makeSaisie(String(base), texte, { ...opts(), strict: true, suffixe: '°' });
        withSvg(payload, svg);
            return payload;
    } else {
        const b = randomChoice(ANGLES_ISOCELE_BASE);
        const sommet = 180 - 2 * b;
        const texte = `<p>Le triangle ${X}${V}${Y} est isocèle en ${X}. L'angle en ${V} mesure ${b}°. Quelle est la mesure, en degrés, de l'angle en ${X} ?</p>{{element:0}}`;
        const svg = svgTriangle([X, V, Y], [sommet, b, b], [false, true, false], { isocele: 0 });
        const payload = makeSaisie(String(sommet), texte, { ...opts(), strict: true, suffixe: '°' });
        withSvg(payload, svg);
            return payload;
    }
};

const genSaisieRectangle = () => {
    const [X, V, Y] = pickLetters(3);
    const a = randomChoice(ANGLES_RECTANGLE);
    const other = 90 - a;
    const texte = `<p>Le triangle ${X}${V}${Y} est rectangle en ${V}. L'angle en ${X} mesure ${a}°. Quelle est la mesure, en degrés, de l'angle en ${Y} ?</p>{{element:0}}`;
    const svg = svgTriangle([X, V, Y], [a, 90, other], [true, true, false], { rightAngleAt: 1 });
    const payload = makeSaisie(String(other), texte, { ...opts(), strict: true, suffixe: '°' });
    withSvg(payload, svg);
    return payload;
};

const genSaisie = () => {
    const variant = randomChoice(['quelconque', 'isocele', 'rectangle']);
    switch (variant) {
        case 'quelconque': return genSaisieQuelconque();
        case 'isocele': return genSaisieIsocele();
        case 'rectangle': return genSaisieRectangle();
    }
};

// ─── TABLEAU V/F ────────────────────────────────────────────

const genVF = () => {
    const [X, V, Y] = pickLetters(3);
    const { a, b, c } = genQuelconque();
    const nat = natureTriangle(a, b, c);
    const natLabel = natureLabel(nat);

    // Determine largest angle vertex
    const anglesArr = [{ v: X, a: a }, { v: V, a: b }, { v: Y, a: c }];
    const largest = anglesArr.reduce((max, cur) => cur.a > max.a ? cur : max, anglesArr[0]);

    // Wrong natures
    const allNatures = ['quelconque', 'isocèle', 'équilatéral', 'rectangle', 'rectangle isocèle'];
    const wrongNats = shuffle(allNatures.filter(n => n !== natLabel));

    // Wrong largest
    const wrongLargest = anglesArr.filter(x => x.v !== largest.v);

    // VRAI pool
    const vraiPool = shuffle([
        `L'angle en ${Y} mesure ${c}°.`,
        `La somme des angles du triangle ${X}${V}${Y} vaut 180°.`,
        `Le triangle ${X}${V}${Y} est ${natLabel}.`,
        `Le plus grand angle est en ${largest.v}.`,
    ]);

    // FAUX pool
    const fauxPool = shuffle([
        `L'angle en ${Y} mesure ${a + b}°.`,
        `L'angle en ${Y} mesure ${180 - a}°.`,
        `L'angle en ${Y} mesure ${360 - a - b}°.`,
        `Le triangle ${X}${V}${Y} est ${wrongNats[0]}.`,
        `La somme des angles du triangle ${X}${V}${Y} vaut 360°.`,
        ...(wrongLargest.length > 0 ? [`Le plus grand angle est en ${wrongLargest[0].v}.`] : []),
    ]);

    // Pick at least 2V and 2F, total 5
    const nbVrais = randInt(2, 3);
    const nbFaux = 5 - nbVrais;

    // Dedup with Set
    const used = new Set();
    const selectedVrais = [];
    for (const v of vraiPool) {
        if (selectedVrais.length >= nbVrais) break;
        if (!used.has(v)) { used.add(v); selectedVrais.push(v); }
    }
    const selectedFaux = [];
    for (const f of fauxPool) {
        if (selectedFaux.length >= nbFaux) break;
        if (!used.has(f)) { used.add(f); selectedFaux.push(f); }
    }

    // Verify counts, retry if insufficient
    if (selectedVrais.length < 2 || selectedFaux.length < 2 || selectedVrais.length + selectedFaux.length < 5) {
        return genVF();
    }

    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);

    const lignes = mixed.map(m => m.l);
    const bonnesReponses = mixed.map(m => m.r);

    const texte = `<p>Dans le triangle ${X}${V}${Y}, l'angle en ${X} mesure ${a}° et l'angle en ${V} mesure ${b}°. Indique si chaque affirmation est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts());
    return payload;
};

// ─── TEXTE A TROUS DRAG (3 models) ─────────────────────────

const genTrouDragM1 = () => {
    const [X, V, Y] = pickLetters(3);
    const { a, b, c } = genQuelconque();
    const sommeAB = a + b;
    const phrase = `Dans le triangle ${X}${V}${Y}, les angles en ${X} et ${V} mesurent ${a}° et ${b}°. La somme des angles vaut {{180}}°. L'angle en ${Y} = 180 - (${a} + ${b}) = 180 - {{${sommeAB}}}° = {{${c}}}°.`;
    // Filtre : a, b ou 180-a peuvent égaler l'angle attendu c
    const distracteurs = filtrerDistracteurs(phrase, [`${360}`, `${a}`, `${b}`, `${180 - a}`]);
    const texte = `<p>Complète le raisonnement en glissant les valeurs proposées.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'drag', distracteurs });
    return payload;
};

const genTrouDragM2 = () => {
    const [X, V, Y] = pickLetters(3);
    const s = randomChoice(ANGLES_ISOCELE_SOMMET);
    const base = (180 - s) / 2;
    const phrase = `Le triangle ${X}${V}${Y} est isocèle en ${X}, avec l'angle en ${X} = ${s}°. Les angles de la base sont {{égaux}}. Chacun mesure (180 - ${s}) / 2 = {{${base}}}°.`;
    // Filtre : s/2 égale l'angle de base quand s = 60
    const distracteurs = filtrerDistracteurs(phrase, ['complémentaires', `${180 - s}`, `${s / 2}`, `${s}`]);
    const texte = `<p>Complète le raisonnement en glissant les valeurs proposées.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'drag', distracteurs });
    return payload;
};

const genTrouDragM3 = () => {
    const [X, V, Y] = pickLetters(3);
    const a = randomChoice(ANGLES_RECTANGLE);
    const other = 90 - a;
    const phrase = `Le triangle ${X}${V}${Y} est rectangle en ${V}. L'angle en ${X} mesure ${a}°. Les angles aigus d'un triangle rectangle sont {{complémentaires}}. L'angle en ${Y} = {{${other}}}°.`;
    // Filtre : quand a = 45, l'angle attendu vaut aussi 45
    const distracteurs = filtrerDistracteurs(phrase, ['supplémentaires', `${180 - a}`, `${a}`, '180']);
    const texte = `<p>Complète le raisonnement en glissant les valeurs proposées.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'drag', distracteurs });
    return payload;
};

const genTrouDrag = () => {
    const model = randomChoice(['m1', 'm2', 'm3']);
    switch (model) {
        case 'm1': return genTrouDragM1();
        case 'm2': return genTrouDragM2();
        case 'm3': return genTrouDragM3();
    }
};

// ─── TEXTE A TROUS SAISIE (4 models) ───────────────────────

const genTrouSaisieM1 = () => {
    const [X, V, Y] = pickLetters(3);
    const { a, b, c } = genQuelconque();
    const phrase = `Dans le triangle ${X}${V}${Y}, l'angle en ${X} mesure ${a}° et l'angle en ${V} mesure ${b}°. L'angle en ${Y} mesure {{${c}}}°.`;
    const texte = `<p>Complète avec la bonne mesure d'angle (en degrés).</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'saisie' });
    return payload;
};

const genTrouSaisieM2 = () => {
    const [X, V, Y] = pickLetters(3);
    const s = randomChoice(ANGLES_ISOCELE_SOMMET);
    const base = (180 - s) / 2;
    const phrase = `Le triangle ${X}${V}${Y} est isocèle en ${X}. L'angle en ${X} mesure ${s}°. Chaque angle de la base mesure {{${base}}}°.`;
    const texte = `<p>Complète avec la bonne mesure d'angle (en degrés).</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'saisie' });
    return payload;
};

const genTrouSaisieM3 = () => {
    const [X, V, Y] = pickLetters(3);
    const b = randomChoice(ANGLES_ISOCELE_BASE);
    const sommet = 180 - 2 * b;
    const phrase = `Le triangle ${X}${V}${Y} est isocèle en ${X}. L'angle en ${V} mesure ${b}°. L'angle au sommet en ${X} mesure {{${sommet}}}°.`;
    const texte = `<p>Complète avec la bonne mesure d'angle (en degrés).</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'saisie' });
    return payload;
};

const genTrouSaisieM4 = () => {
    const [X, V, Y] = pickLetters(3);
    const a = randomChoice(ANGLES_RECTANGLE);
    const other = 90 - a;
    const phrase = `Le triangle ${X}${V}${Y} est rectangle en ${V}. L'angle en ${X} mesure ${a}°. L'angle en ${Y} mesure {{${other}}}°.`;
    const texte = `<p>Complète avec la bonne mesure d'angle (en degrés).</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'saisie' });
    return payload;
};

const genTrouSaisie = () => {
    const model = randomChoice(['m1', 'm2', 'm3', 'm4']);
    switch (model) {
        case 'm1': return genTrouSaisieM1();
        case 'm2': return genTrouSaisieM2();
        case 'm3': return genTrouSaisieM3();
        case 'm4': return genTrouSaisieM4();
    }
};

// ═══════════════════════════════════════════════════════════
// Constructibilité d'un triangle à partir de la somme des angles
// ═══════════════════════════════════════════════════════════

// Pool 1 — Cas pédagogiques curés (triangles particuliers + pièges décimaux).
// Ce pool garantit l'apparition régulière de cas remarquables dans les exos.
const POOL_PEDAGO_CSTR = [
    // Triangles particuliers constructibles
    { angles: [60, 60, 60], constructible: true,  corrigeABC: null },
    { angles: [30, 60, 90], constructible: true,  corrigeABC: null },
    { angles: [90, 45, 45], constructible: true,  corrigeABC: null },
    { angles: [50, 60, 70], constructible: true,  corrigeABC: null },
    { angles: [40, 60, 80], constructible: true,  corrigeABC: null },
    // Cas non constructibles entiers
    { angles: [68, 27, 75], constructible: false, corrigeABC: 78 },   // 68+27+75=170 → ABC=78
    { angles: [43, 58, 101], constructible: false, corrigeABC: 21 },  // 43+58+101=202 → ABC=21
    { angles: [80, 70, 65], constructible: false, corrigeABC: 45 },   // 215 → ABC=45
    { angles: [35, 50, 60], constructible: false, corrigeABC: 70 },   // 145 → ABC=70
    // Pièges décimaux "presque 180°"
    { angles: [62.1, 72.8, 45], constructible: false, corrigeABC: 62.2 },  // 179.9 → ABC=62.2
    { angles: [34.5, 82, 63.5], constructible: true,  corrigeABC: null },  // exactement 180
];

// Pool 2 — Générateur dynamique de triplets constructibles (somme = 180°).
const genTripletConstructible = () => {
    const a = randInt(20, 80);
    const b = randInt(20, 180 - a - 20);
    const c = 180 - a - b;
    return { angles: [a, b, c], constructible: true, corrigeABC: null };
};

// Pool 3 — Générateur dynamique de triplets non constructibles (somme ≠ 180°).
const genTripletNonConstructible = () => {
    const delta = randomChoice([-30, -25, -20, -15, -10, -5, 10, 15, 20, 25, 30, 40]);
    const sommeCible = 180 + delta;
    let tries = 0;
    while (tries++ < 20) {
        const a = randInt(20, 80);
        const b = randInt(20, 80);
        const c = sommeCible - a - b;
        // L'angle corrige vaut exactement a - delta, puisque b + c = 180 + delta - a.
        // Sans cette borne, un delta de +40 sur un a de 29 reclamait un angle de
        // -11 degres : l'eleve devait saisir une mesure negative pour etre compte
        // juste (1,72 % des tirages).
        const corrige = 180 - b - c;
        if (c >= 10 && c <= 150 && corrige >= 10 && corrige <= 150) {
            return { angles: [a, b, c], constructible: false, corrigeABC: corrige };
        }
    }
    return { angles: [30, 40, 60], constructible: false, corrigeABC: 80 }; // fallback
};

// Tirage centralisé : 20% pédago curé, 40% constructible aléatoire, 40% non-constr aléatoire.
const tireTripletConstr = () => {
    const r = Math.random();
    if (r < 0.2) return randomChoice(POOL_PEDAGO_CSTR);
    if (r < 0.6) return genTripletConstructible();
    return genTripletNonConstructible();
};

const L_CSTR_SAISIE = 'Constructibilité d\'un triangle (somme des angles)';
const L_CSTR_VF = 'Constructibilité de plusieurs triangles (V/F)';

// Format 1 : texte à trous saisie — 1 triangle par exo, calcul guidé étape par étape.
const genConstructibiliteTrouSaisie = () => {
    const [L1, L2, L3] = pickLetters(3);
    const triplet = tireTripletConstr();
    const [a1, a2, a3] = triplet.angles;
    const somme = Math.round((a1 + a2 + a3) * 10) / 10; // arrondi 1 décimale max

    const texte = `<p>Le triangle ${L1}${L2}${L3} a pour mesures d'angles : `
        + `$\\widehat{${L1}${L2}${L3}}$ = ${a1}°, $\\widehat{${L2}${L3}${L1}}$ = ${a2}°, `
        + `$\\widehat{${L3}${L1}${L2}}$ = ${a3}°.</p>`
        + `<p><em>Calcule la somme des angles, indique si le triangle est constructible, `
        + `et si besoin corrige la mesure de l'angle $\\widehat{${L1}${L2}${L3}}$.</em></p>`;

    // Le trou lexical attend UN SEUL MOT, sans apostrophe : `compareAnswers`
    // normalise la casse et les accents, pas la ponctuation. « n'est pas »
    // était refusé dès que l'élève tapait « n est pas » ou l'apostrophe
    // typographique, sur la moitié des tirages.
    let phrase;
    if (triplet.constructible) {
        phrase = `La somme des angles vaut {{${somme}}}°, donc le triangle ${L1}${L2}${L3} est {{constructible}}.`;
    } else {
        phrase = `La somme des angles vaut {{${somme}}}°, donc le triangle ${L1}${L2}${L3} est {{impossible}} à construire. `
            + `Pour le rendre constructible, l'angle $\\widehat{${L1}${L2}${L3}}$ doit mesurer {{${triplet.corrigeABC}}}°.`;
    }

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_CSTR_SAISIE),
        label: L_CSTR_SAISIE,
        mode: 'saisie',
        strict: true,
    });
    payload._meta.generatorKey = 'constructibilite_somme_angles_texte_trou_saisie';
    return payload;
};

// Format 2 : tableau V/F — 4 triangles en batch, classer Constructible / Non constructible.
const genConstructibiliteTableauVF = () => {
    // Tirage équilibré : 2 constructibles + 2 non constructibles (ordre aléatoire).
    const triplets = [];
    for (let i = 0; i < 2; i++) {
        const r = Math.random();
        triplets.push(r < 0.3 ? randomChoice(POOL_PEDAGO_CSTR.filter(p => p.constructible)) : genTripletConstructible());
    }
    for (let i = 0; i < 2; i++) {
        const r = Math.random();
        triplets.push(r < 0.3 ? randomChoice(POOL_PEDAGO_CSTR.filter(p => !p.constructible)) : genTripletNonConstructible());
    }
    const melange = shuffle(triplets);

    const lignes = melange.map((t) => {
        const [L1, L2, L3] = pickLetters(3);
        const [a1, a2, a3] = t.angles;
        return `Triangle ${L1}${L2}${L3} : $\\widehat{${L1}${L2}${L3}}$ = ${a1}°, $\\widehat{${L2}${L3}${L1}}$ = ${a2}°, $\\widehat{${L3}${L1}${L2}}$ = ${a3}°`;
    });
    // 0 = Constructible (col 0), 1 = Non constructible (col 1)
    const bonnesReponses = melange.map((t) => t.constructible ? 0 : 1);

    const texte = `<p>Pour chaque ligne, indique si le triangle donné est <b>constructible</b>. `
        + `<em>Rappel : la somme des angles d'un triangle vaut 180°.</em></p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_CSTR_VF),
        label: L_CSTR_VF,
        colonnes: ['Constructible', 'Non constructible'],
    });
    payload._meta.generatorKey = 'constructibilite_somme_angles_tableau_vf';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Alignement de points via somme des angles (figure avec triangle intérieur)
// ═══════════════════════════════════════════════════════════

const L_ALIGN_QCM = 'Alignement de 3 points (QCM)';
const L_ALIGN_TROU = 'Alignement de 3 points (saisie guidée)';

/**
 * Génère une configuration d'angles cohérente pour la figure "DCA avec E sur [DA]
 * + triangle CEB équilatéral + angle droit en B". Contrainte d'alignement :
 * angleDEC + 60° + (90° − angleA) = 180°  ⟹  angleD + angleC = 150° − angleA.
 */
const genConfigAlignement = (aligne) => {
    // angle en A (triangle rectangle en B) : tiré parmi des valeurs pédagogiques
    // Plus de valeurs à deux décimales : 54,38 et 40,2 traînaient leurs
    // centièmes dans toute la démonstration, et l'élève devait saisir trois
    // nombres au centième près. 62,5 est conservé : une décimale suffit à
    // garder le cas non entier, qui a son intérêt.
    const angleA = randomChoice([30, 35, 40, 45, 50, 55, 60, 62.5]);
    // répartition de l'angle complémentaire entre D et C
    const sumDC = 150 - angleA;
    const angleD = Math.round((sumDC * (0.4 + Math.random() * 0.3)) * 100) / 100;
    const angleC = Math.round((sumDC - angleD) * 100) / 100;
    // Si non aligné : on perturbe légèrement l'un des angles
    if (!aligne) {
        const delta = randomChoice([-12, -8, -5, 5, 8, 12]);
        const cible = randomChoice(['A', 'D', 'C']);
        return {
            angleD: cible === 'D' ? Math.round((angleD + delta) * 100) / 100 : angleD,
            angleC: cible === 'C' ? Math.round((angleC + delta) * 100) / 100 : angleC,
            angleA: cible === 'A' ? Math.round((angleA + delta) * 100) / 100 : angleA,
            aligne: false,
        };
    }
    return { angleD, angleC, angleA, aligne: true };
};

/**
 * SVG de la figure : triangle DCA + point E sur [DA] + triangle CEB équilatéral
 * interne + angle droit marqué en B. Coordonnées fixes (seuls les labels d'angles
 * et de points varient d'un exo à l'autre).
 */
const svgAlignement = (L_D, L_C, L_E, L_B, L_A, angleD, angleC, angleA, salt) => {
    // Positions calculées pour garantir :
    //   - [DA] horizontal, E sur [DA]
    //   - Triangle CEB équilatéral (CE = CB = EB) ← marques ×
    //   - Triangle EBA rectangle en B (BE ⊥ BA) ← petit carré
    // On part de D=(80,280), E=(340,280), CE vertical (C au-dessus de E), longueur CE = 200.
    // Avec ces contraintes, B et A sont déterminés mathématiquement.
    const D = { x: 80, y: 280 };
    const E = { x: 340, y: 280 };
    const C = { x: 340, y: 80 };      // CE vertical, longueur 200
    const B = { x: 513, y: 180 };     // milieu(CE) + perp(CE) * 200*√3/2 → équilatéral
    const A = { x: 571, y: 280 };     // B + perp(BE) → BA ⊥ BE et A sur y=280

    // Petit tick d'égalité au milieu d'un segment
    const tick = (P1, P2) => {
        const mx = (P1.x + P2.x) / 2, my = (P1.y + P2.y) / 2;
        const dx = P2.x - P1.x, dy = P2.y - P1.y;
        const L = Math.hypot(dx, dy);
        const px = (-dy / L) * 6, py = (dx / L) * 6;
        return `<line x1='${(mx - px).toFixed(1)}' y1='${(my - py).toFixed(1)}' x2='${(mx + px).toFixed(1)}' y2='${(my + py).toFixed(1)}' stroke='black' stroke-width='1.5'/>`;
    };

    // Carré d'angle droit en B (entre BC et BE... ici on le place entre BE et BA)
    const rightAngle = () => {
        const size = 10;
        const uBE = { x: (E.x - B.x), y: (E.y - B.y) };
        const lBE = Math.hypot(uBE.x, uBE.y);
        uBE.x /= lBE; uBE.y /= lBE;
        const uBA = { x: (A.x - B.x), y: (A.y - B.y) };
        const lBA = Math.hypot(uBA.x, uBA.y);
        uBA.x /= lBA; uBA.y /= lBA;
        const P1 = { x: B.x + uBE.x * size, y: B.y + uBE.y * size };
        const P2 = { x: B.x + uBA.x * size, y: B.y + uBA.y * size };
        const P3 = { x: P1.x + uBA.x * size, y: P1.y + uBA.y * size };
        return `<path d='M ${P1.x.toFixed(1)},${P1.y.toFixed(1)} L ${P3.x.toFixed(1)},${P3.y.toFixed(1)} L ${P2.x.toFixed(1)},${P2.y.toFixed(1)}' fill='none' stroke='#E24B4A' stroke-width='1.3'/>`;
    };

    // Arc coloré pour afficher un angle à un sommet + texte de mesure
    const angleArc = (vertex, p1, p2, color, label, rBase = 32) => {
        const v1 = { x: p1.x - vertex.x, y: p1.y - vertex.y };
        const v2 = { x: p2.x - vertex.x, y: p2.y - vertex.y };
        const a1 = Math.atan2(v1.y, v1.x);
        const a2 = Math.atan2(v2.y, v2.x);
        const start = { x: vertex.x + rBase * Math.cos(a1), y: vertex.y + rBase * Math.sin(a1) };
        const end = { x: vertex.x + rBase * Math.cos(a2), y: vertex.y + rBase * Math.sin(a2) };
        let delta = a2 - a1;
        while (delta > Math.PI) delta -= 2 * Math.PI;
        while (delta < -Math.PI) delta += 2 * Math.PI;
        const largeArc = Math.abs(delta) > Math.PI ? 1 : 0;
        const sweep = delta > 0 ? 1 : 0;
        const midAngle = a1 + delta / 2;
        const textR = rBase + 22;
        const tx = vertex.x + textR * Math.cos(midAngle);
        const ty = vertex.y + textR * Math.sin(midAngle);
        return `<path d='M ${start.x.toFixed(1)},${start.y.toFixed(1)} A ${rBase} ${rBase} 0 ${largeArc} ${sweep} ${end.x.toFixed(1)},${end.y.toFixed(1)}' fill='none' stroke='${color}' stroke-width='1.5'/>`
            + `<text x='${tx.toFixed(1)}' y='${(ty + 4).toFixed(1)}' font-size='12' text-anchor='middle' fill='${color}' font-weight='600'>${label}</text>`;
    };

    const lbl = (p, txt, dx = 0, dy = 0) =>
        `<text x='${(p.x + dx).toFixed(1)}' y='${(p.y + dy).toFixed(1)}' font-size='15' font-weight='700'>${txt}</text>`;

    return `<svg data-v='${salt}' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 680 340' width='680' height='340'>`
        // Côté gauche du triangle DCE + base complète [DA] (E sur [DA])
        + `<line x1='${D.x}' y1='${D.y}' x2='${C.x}' y2='${C.y}' stroke='black' stroke-width='1.8'/>`
        + `<line x1='${D.x}' y1='${D.y}' x2='${A.x}' y2='${A.y}' stroke='black' stroke-width='1.8'/>`
        // Triangle CEB équilatéral interne
        + `<line x1='${C.x}' y1='${C.y}' x2='${E.x}' y2='${E.y}' stroke='black' stroke-width='1.8'/>`
        + `<line x1='${C.x}' y1='${C.y}' x2='${B.x}' y2='${B.y}' stroke='black' stroke-width='1.8'/>`
        + `<line x1='${E.x}' y1='${E.y}' x2='${B.x}' y2='${B.y}' stroke='black' stroke-width='1.8'/>`
        // Côté [BA] (ferme le triangle EBA rectangle en B)
        + `<line x1='${B.x}' y1='${B.y}' x2='${A.x}' y2='${A.y}' stroke='black' stroke-width='1.8'/>`
        // Marques d'égalité (× sur CE, CB, EB → triangle équilatéral)
        + tick(C, E) + tick(C, B) + tick(E, B)
        // Angle droit en B
        + rightAngle()
        // Arcs + mesures aux sommets
        + angleArc(D, A, C, '#D946EF', `${angleD}°`, 40)
        + angleArc(C, E, D, '#2563EB', `${angleC}°`, 30)
        + angleArc(A, B, D, '#10B981', `${angleA}°`, 40)
        // Labels des points
        + lbl(D, L_D, -16, 6)
        + lbl(E, L_E, -5, 22)
        + lbl(A, L_A, 8, 6)
        + lbl(C, L_C, -5, -8)
        + lbl(B, L_B, 10, 0)
        + `</svg>`;
};

const genAlignementQcm = () => {
    const [L_D, L_C, L_E, L_B, L_A] = pickLetters(5);
    const aligne = Math.random() < 0.5;
    const cfg = genConfigAlignement(aligne);
    const salt = randInt(1, 99999);
    const svg = svgAlignement(L_D, L_C, L_E, L_B, L_A, cfg.angleD, cfg.angleC, cfg.angleA, salt);
    const imageData = svgToDataUrl(svg);

    const texte = `<p>En observant la figure ci-dessous (qui n'est pas en vraie grandeur), `
        + `les points ${L_D}, ${L_E} et ${L_A} sont-ils alignés ?</p>{{element:0}}`;

    const choix = ['Oui, alignés', 'Non, pas alignés', 'On ne peut pas conclure'];
    const bonne = aligne ? 'Oui, alignés' : 'Non, pas alignés';

    const payload = makeQcm(choix, bonne, texte, opts(L_ALIGN_QCM));
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'image', config: { imageData } }],
    };
    // FLAG CALCULATRICE. Un générateur autorise la calculatrice format par format
    // en posant le drapeau dans `contenu` — impérativement là, jamais dans `_meta`
    // qui est supprimé à la sauvegarde. Il voyage ainsi avec la question jusqu'à
    // l'espace élève. Il ne déclenche encore aucun affichage : le badge
    // « calculatrice autorisée » est un chantier UI à venir.
    //
    // Ici l'objet de l'exercice est de savoir QUELS angles additionner, pas de
    // poser l'opération : la calculatrice libère le raisonnement sans rien
    // donner. Un format qui testerait la technique opératoire ne l'autoriserait
    // pas.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'alignement_points_qcm_unique';
    return payload;
};

const genAlignementTrouSaisie = () => {
    const [L_D, L_C, L_E, L_B, L_A] = pickLetters(5);
    const aligne = Math.random() < 0.5;
    const cfg = genConfigAlignement(aligne);
    const salt = randInt(1, 99999);
    const svg = svgAlignement(L_D, L_C, L_E, L_B, L_A, cfg.angleD, cfg.angleC, cfg.angleA, salt);
    const imageData = svgToDataUrl(svg);

    const angleDEC = Math.round((180 - cfg.angleD - cfg.angleC) * 100) / 100;
    const angleBEA = Math.round((90 - cfg.angleA) * 100) / 100;
    const somme = Math.round((angleDEC + 60 + angleBEA) * 100) / 100;

    const texte = `<p>En observant la figure ci-dessous (qui n'est pas en vraie grandeur), `
        + `démontre si les points ${L_D}, ${L_E}, ${L_A} sont alignés ou non.</p>{{element:0}}`;

    const phrase =
        `Dans le triangle ${L_D}${L_C}${L_E} : $\\widehat{${L_D}${L_E}${L_C}}$ = 180° − ${cfg.angleD}° − ${cfg.angleC}° = {{${angleDEC}}}°.\n`
        + `Le triangle ${L_C}${L_E}${L_B} est {{équilatéral}} (trois côtés marqués égaux), donc $\\widehat{${L_C}${L_E}${L_B}}$ = {{60}}°.\n`
        + `Dans le triangle ${L_E}${L_B}${L_A} rectangle en ${L_B} : $\\widehat{${L_B}${L_E}${L_A}}$ = 180° − 90° − ${cfg.angleA}° = {{${angleBEA}}}°.\n`
        + `Somme des trois angles en ${L_E} : {{${somme}}}°.\n`
        // La conclusion reste à la charge de l'élève : c'est le trou qui la porte.
        // La parenthèse juste avant lui donne les deux formulations possibles, de
        // sorte qu'il n'ait plus qu'à choisir — sans avoir à deviner l'orthographe
        // exacte de « ne sont pas », qui était refusée à la moindre variante.
        + `Les points ${L_D}, ${L_E}, ${L_A} (sont ou ne sont pas) {{${aligne ? 'sont' : 'ne sont pas'}}} alignés.`;

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_ALIGN_TROU),
        label: L_ALIGN_TROU,
        mode: 'saisie',
        strict: true,
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    // FLAG CALCULATRICE — même raison qu'au format QCM ci-dessus : l'exercice
    // porte sur le choix des angles à additionner, pas sur la technique
    // opératoire. Voir le commentaire de `genAlignementQcm` pour la convention.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'alignement_points_texte_trou_saisie';
    return payload;
};

// ─── Registry & Export ──────────────────────────────────────

const GENERATORS = {
    calcul_angles_triangle_qcm_unique: genQcm,
    calcul_angles_triangle_texte: genSaisie,
    calcul_angles_triangle_tableau_vf: genVF,
    calcul_angles_triangle_texte_trou_drag: genTrouDrag,
    calcul_angles_triangle_texte_trou_saisie: genTrouSaisie,
    constructibilite_somme_angles_texte_trou_saisie: genConstructibiliteTrouSaisie,
    constructibilite_somme_angles_tableau_vf: genConstructibiliteTableauVF,
    alignement_points_qcm_unique: genAlignementQcm,
    alignement_points_texte_trou_saisie: genAlignementTrouSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    texte: 'Saisie',
    tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'calcul_angles_triangle',
        label: 'Calcul d\'angles dans un triangle',
        formatsList: buildFormats('calcul_angles_triangle'),
    },
    {
        id: 'constructibilite_somme_angles',
        label: 'Constructibilité d\'un triangle (somme des angles)',
        formatsList: buildFormats('constructibilite_somme_angles'),
    },
    {
        id: 'alignement_points',
        label: 'Alignement de 3 points (problème avec figure)',
        formatsList: buildFormats('alignement_points'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du REGISTRE fait foi, toujours : c'est elle qui est persistée dans
    // `generator_key` et qui identifie le format. Le `if (!...)` était une garde
    // morte — les sous-générateurs posaient déjà une clé locale commune, si bien
    // que les neuf formats partageaient une seule et même clé.
    payload._meta.generatorKey = key;
    return payload;
};
